import { useState, useEffect } from 'react';
import adminService from '../../../services/adminService';
import './AdminAdmins.css';

const AdminAdmins = () => {
  const [admins, setAdmins] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editAdminData, setEditAdminData] = useState(null);
  const [newAdmin, setNewAdmin] = useState({
    username: '',
    email: '',
    password: '',
    maxTimelines: 99,
    role: 'admin',
    permissions: [],
    canCreateGroup: false,
    maxEditors: 0,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminService.getAllUsers();
      setAllUsers(data);
      setAdmins(data.filter((u) => u.isAdmin || u.isSuperAdmin));
    } catch (err) {
      setError('Veriler yüklenemedi. ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newAdmin.username || !newAdmin.email || !newAdmin.password) {
      setError('Tüm alanları doldurun.');
      return;
    }

    try {
      await adminService.createAdminUser({
        username: newAdmin.username,
        email: newAdmin.email,
        password: newAdmin.password,
        maxTimelines: parseInt(newAdmin.maxTimelines, 10) || 99,
        isAdmin: true,
        isSuperAdmin: newAdmin.role === 'superadmin',
        role: newAdmin.role,
        permissions: newAdmin.permissions,
        canCreateGroup: newAdmin.canCreateGroup,
        maxEditors: parseInt(newAdmin.maxEditors, 10) || 0,
      });
      setSuccess(`"${newAdmin.username}" yönetici olarak eklendi.`);
      setShowAddForm(false);
      setNewAdmin({ username: '', email: '', password: '', maxTimelines: 99, role: 'admin' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Yönetici eklenemedi.');
    }
  };

  const handleToggleSuperAdmin = async (userId, currentValue) => {
    try {
      setError('');
      await adminService.updateUserRole(userId, { isSuperAdmin: !currentValue, isAdmin: true });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Yetki güncellenemedi.');
    }
  };

  const handleRemoveAdmin = async (userId, username) => {
    if (!window.confirm(`"${username}" kullanıcısının admin yetkisini kaldırmak istediğinizden emin misiniz?`)) return;
    try {
      setError('');
      await adminService.updateUserRole(userId, { isAdmin: false, isSuperAdmin: false });
      setSuccess(`"${username}" artık yönetici değil.`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Yetki kaldırılamadı.');
    }
  };

  const handleDeleteAdmin = async (userId, username) => {
    if (!window.confirm(`"${username}" kullanıcısını tamamen silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) return;
    try {
      setError('');
      await adminService.deleteUser(userId);
      setSuccess(`"${username}" silindi.`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Silinemedi.');
    }
  };

  const handleEditAdmin = (admin) => {
    setEditAdminData({
      _id: admin._id,
      username: admin.username,
      email: admin.email,
      isAdmin: admin.isAdmin,
      isSuperAdmin: admin.isSuperAdmin,
      newPassword: '',
      permissions: admin.permissions || [],
      canCreateGroup: admin.canCreateGroup || false,
      maxEditors: admin.maxEditors || 0,
      role: admin.role || (admin.isSuperAdmin ? 'superadmin' : 'admin'),
    });
    setShowEditForm(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const profileData = {};
      if (editAdminData.username) profileData.username = editAdminData.username;
      if (editAdminData.email) profileData.email = editAdminData.email;
      if (editAdminData.newPassword) profileData.password = editAdminData.newPassword;

      if (Object.keys(profileData).length > 0) {
        await adminService.updateUserProfile(editAdminData._id, profileData);
      }
      await adminService.updateUserRole(editAdminData._id, {
        isAdmin: editAdminData.isAdmin,
        isSuperAdmin: editAdminData.isSuperAdmin,
      });
      await adminService.updateUserPermissions(editAdminData._id, {
        permissions: editAdminData.permissions,
        canCreateGroup: editAdminData.canCreateGroup,
        maxEditors: parseInt(editAdminData.maxEditors, 10) || 0,
        role: editAdminData.role,
      });
      setSuccess(`"${editAdminData.username}" güncellendi.`);
      setShowEditForm(false);
      setEditAdminData(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Güncelleme başarısız.');
    }
  };

  const toggleAdminPermission = (perm) => {
    const current = editAdminData.permissions || [];
    if (current.includes(perm)) {
      setEditAdminData({ ...editAdminData, permissions: current.filter(p => p !== perm) });
    } else {
      setEditAdminData({ ...editAdminData, permissions: [...current, perm] });
    }
  };

  if (loading) {
    return <div className="admin-admins-loading">Yükleniyor...</div>;
  }

  return (
    <div className="admin-admins">
      {success && <div className="admin-admins-success">{success}</div>}
      {error && <div className="admin-admins-error">{error}</div>}

      <div className="admin-admins-card">
        <div className="admin-admins-header">
          <h3>Yöneticiler ({admins.length})</h3>
          <button className="admin-admins-add-btn" onClick={() => setShowAddForm(!showAddForm)}>
            + Yeni Yönetici Ekle
          </button>
        </div>

        {showAddForm && (
          <form className="admin-admins-form" onSubmit={handleAddAdmin}>
            <h4>Yeni Yönetici Ekle</h4>
            <div className="admin-admins-form-grid">
              <div className="admin-admins-form-group">
                <label>Kullanıcı Adı</label>
                <input
                  type="text"
                  value={newAdmin.username}
                  onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                  required
                />
              </div>
              <div className="admin-admins-form-group">
                <label>E-posta</label>
                <input
                  type="email"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  required
                />
              </div>
              <div className="admin-admins-form-group">
                <label>Şifre</label>
                <input
                  type="password"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  required
                />
              </div>
              <div className="admin-admins-form-group">
                <label>Yetki Seviyesi</label>
                <select
                  value={newAdmin.role}
                  onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}
                >
                  <option value="admin">Admin</option>
                  <option value="superadmin">Süper Admin</option>
                </select>
              </div>
            </div>

            {/* Modül Yetkileri */}
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#1f2937', borderRadius: '0.5rem' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.75rem', display: 'block', color: '#f3f4f6' }}>Modül Yetkileri</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                {[
                  { key: 'slider', label: 'Slider' },
                  { key: 'boxes', label: 'Kutucuklar' },
                  { key: 'texts', label: 'Metin Bölümleri' },
                  { key: 'features', label: 'Özellikler' },
                  { key: 'user-management', label: 'Kullanıcı Yönetimi' },
                  { key: 'admin-management', label: 'Yönetici Yönetimi' },
                  { key: 'invitation-codes', label: 'Davet Kodları' },
                  { key: 'project-management', label: 'Proje Yönetimi' },
                ].map(mod => (
                  <label key={mod.key} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={newAdmin.permissions.includes(mod.key)} onChange={() => {
                      const perms = newAdmin.permissions.includes(mod.key) ? newAdmin.permissions.filter(p => p !== mod.key) : [...newAdmin.permissions, mod.key];
                      setNewAdmin({ ...newAdmin, permissions: perms });
                    }} style={{ accentColor: '#2563eb' }} />
                    <span style={{ fontSize: '0.8rem', color: '#d1d5db' }}>{mod.label}</span>
                  </label>
                ))}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', marginTop: '0.5rem', borderTop: '1px solid #374151', paddingTop: '0.5rem' }}>
                <input type="checkbox" checked={newAdmin.permissions.includes('all')} onChange={() => {
                  const perms = newAdmin.permissions.includes('all') ? newAdmin.permissions.filter(p => p !== 'all') : [...newAdmin.permissions, 'all'];
                  setNewAdmin({ ...newAdmin, permissions: perms });
                }} style={{ accentColor: '#2563eb' }} />
                <span style={{ fontSize: '0.8rem', color: '#f3f4f6', fontWeight: '600' }}>Tüm Yetkiler</span>
              </label>
            </div>

            {/* Grup Yetkisi */}
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#1f2937', borderRadius: '0.5rem' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: '#f3f4f6' }}>Grup Yönetimi</label>
              <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={newAdmin.canCreateGroup} onChange={(e) => setNewAdmin({ ...newAdmin, canCreateGroup: e.target.checked })} style={{ accentColor: '#2563eb' }} />
                  <span style={{ fontSize: '0.8rem', color: '#d1d5db' }}>Grup Kurma Yetkisi</span>
                </label>
                {newAdmin.canCreateGroup && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Editör Limiti:</span>
                    <input type="number" value={newAdmin.maxEditors} onChange={(e) => setNewAdmin({ ...newAdmin, maxEditors: e.target.value })} min="0" style={{ width: '4rem', padding: '0.25rem 0.5rem', backgroundColor: '#374151', border: '1px solid #4b5563', borderRadius: '0.25rem', color: '#fff', fontSize: '0.8rem' }} />
                  </div>
                )}
              </div>
            </div>

            <div className="admin-admins-form-actions">
              <button type="submit" className="admin-admins-form-save">Ekle</button>
              <button type="button" className="admin-admins-form-cancel" onClick={() => setShowAddForm(false)}>İptal</button>
            </div>
          </form>
        )}

        {showEditForm && editAdminData && (
          <form className="admin-admins-form" onSubmit={handleSaveEdit}>
            <h4>Düzenle: {editAdminData.username}</h4>
            <div className="admin-admins-form-grid">
              <div className="admin-admins-form-group">
                <label>Kullanıcı Adı</label>
                <input type="text" value={editAdminData.username} onChange={(e) => setEditAdminData({ ...editAdminData, username: e.target.value })} />
              </div>
              <div className="admin-admins-form-group">
                <label>E-posta</label>
                <input type="email" value={editAdminData.email} onChange={(e) => setEditAdminData({ ...editAdminData, email: e.target.value })} />
              </div>
              <div className="admin-admins-form-group">
                <label>Yeni Şifre (boş bırakırsan değişmez)</label>
                <input type="password" value={editAdminData.newPassword} onChange={(e) => setEditAdminData({ ...editAdminData, newPassword: e.target.value })} placeholder="••••••" />
              </div>
              <div className="admin-admins-form-group">
                <label>Rol</label>
                <select value={editAdminData.role} onChange={(e) => setEditAdminData({ ...editAdminData, role: e.target.value })} style={{ width: '100%', padding: '0.5rem', backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '0.375rem', color: '#fff' }}>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Süper Admin</option>
                </select>
              </div>
            </div>

            {/* Modül Yetkileri */}
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#1f2937', borderRadius: '0.5rem' }}>
              <label style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.75rem', display: 'block', color: '#f3f4f6' }}>Modül Yetkileri (Erişebileceği Bölümler)</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                {[
                  { key: 'slider', label: 'Slider Yönetimi' },
                  { key: 'boxes', label: 'Kutucuklar Yönetimi' },
                  { key: 'texts', label: 'Metin Bölümleri' },
                  { key: 'features', label: 'Özellikler' },
                  { key: 'user-management', label: 'Kullanıcı Yönetimi' },
                  { key: 'admin-management', label: 'Yönetici Yönetimi' },
                  { key: 'invitation-codes', label: 'Davet Kodları' },
                  { key: 'project-management', label: 'Proje Yönetimi' },
                ].map(mod => (
                  <label key={mod.key} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={(editAdminData.permissions || []).includes(mod.key)} onChange={() => toggleAdminPermission(mod.key)} style={{ accentColor: '#2563eb' }} />
                    <span style={{ fontSize: '0.8rem', color: '#d1d5db' }}>{mod.label}</span>
                  </label>
                ))}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', marginTop: '0.75rem', borderTop: '1px solid #374151', paddingTop: '0.75rem' }}>
                <input type="checkbox" checked={(editAdminData.permissions || []).includes('all')} onChange={() => toggleAdminPermission('all')} style={{ accentColor: '#2563eb' }} />
                <span style={{ fontSize: '0.85rem', color: '#f3f4f6', fontWeight: '600' }}>Tüm Yetkiler</span>
              </label>
            </div>

            {/* Grup Yetkisi */}
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#1f2937', borderRadius: '0.5rem' }}>
              <label style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.75rem', display: 'block', color: '#f3f4f6' }}>Grup Yönetimi</label>
              <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={editAdminData.canCreateGroup} onChange={(e) => setEditAdminData({ ...editAdminData, canCreateGroup: e.target.checked })} style={{ accentColor: '#2563eb' }} />
                  <span style={{ fontSize: '0.8rem', color: '#d1d5db' }}>Grup Kurma Yetkisi</span>
                </label>
                {editAdminData.canCreateGroup && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Editör Limiti:</span>
                    <input type="number" value={editAdminData.maxEditors} onChange={(e) => setEditAdminData({ ...editAdminData, maxEditors: e.target.value })} min="0" style={{ width: '4rem', padding: '0.25rem 0.5rem', backgroundColor: '#374151', border: '1px solid #4b5563', borderRadius: '0.25rem', color: '#fff', fontSize: '0.8rem' }} />
                  </div>
                )}
              </div>
            </div>

            {/* Eski Yetkilendirme */}
            <div style={{ marginTop: '1rem' }}>
              <label style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Yetkilendirme</label>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={editAdminData.isAdmin} onChange={(e) => setEditAdminData({ ...editAdminData, isAdmin: e.target.checked })} style={{ accentColor: '#2563eb' }} />
                  <span style={{ fontSize: '0.8rem', color: '#d1d5db' }}>Admin</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={editAdminData.isSuperAdmin} onChange={(e) => setEditAdminData({ ...editAdminData, isSuperAdmin: e.target.checked })} style={{ accentColor: '#2563eb' }} />
                  <span style={{ fontSize: '0.8rem', color: '#d1d5db' }}>Süper Admin</span>
                </label>
              </div>
            </div>

            <div className="admin-admins-form-actions">
              <button type="submit" className="admin-admins-form-save">Kaydet</button>
              <button type="button" className="admin-admins-form-cancel" onClick={() => { setShowEditForm(false); setEditAdminData(null); }}>İptal</button>
            </div>
          </form>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table className="admin-admins-table">
            <thead>
              <tr>
                <th>Kullanıcı Adı</th>
                <th>E-posta</th>
                <th>Yetki</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin._id}>
                  <td>{admin.username}</td>
                  <td>{admin.email}</td>
                  <td>
                    {admin.isSuperAdmin ? (
                      <span className="admin-admins-role-badge super-admin">Süper Admin</span>
                    ) : (
                      <span className="admin-admins-role-badge admin">Admin</span>
                    )}
                  </td>
                  <td>
                    <div className="admin-admins-actions">
                      <button
                        className="admin-admins-action-btn role"
                        onClick={() => handleEditAdmin(admin)}
                        title="Düzenle"
                      >
                        Düzenle
                      </button>
                      {!admin.isSuperAdmin && (
                        <>
                          <button
                            className="admin-admins-action-btn role"
                            onClick={() => handleRemoveAdmin(admin._id, admin.username)}
                            title="Admin yetkisini kaldır"
                          >
                            Yetkiyi Al
                          </button>
                          <button
                            className="admin-admins-action-btn delete"
                            onClick={() => handleDeleteAdmin(admin._id, admin.username)}
                            title="Sil"
                          >
                            Sil
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminAdmins;
